import React, { useState, useEffect, useCallback } from "react";
import {
  FaPlus,
  FaEye,
  FaTimes,
  FaBoxes,
  FaBarcode,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaSearch,
  FaSpinner,
  FaCheckCircle,
  FaTrashAlt,
  FaCalculator,
  FaPlayCircle,
  FaExclamationTriangle,
} from "react-icons/fa";
import apiRequest from "../services/api";

const money = (v) => Number(v || 0).toFixed(2);

const AssetManagement = () => {
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedAsset, setSelectedAsset] = useState(null);

  const emptyCreate = {
    id: null,
    assetCode: "",
    name: "",
    description: "",
    purchaseDate: new Date().toISOString().split("T")[0],
    inServiceDate: new Date().toISOString().split("T")[0],
    purchaseCost: 0,
    residualValue: 0,
    exciseAmount: 0,
    vatAmount: 0,
    vatRecoverable: true,
    withholdingAmount: 0,
    vatWithholdingAmount: 0,
    bankCommission: 0.0,
    paymentMethod: "CASH",
    reference: "",
    categoryId: "",
  };

  const [formData, setFormData] = useState(emptyCreate);

  const [activationForm, setActivationForm] = useState({
    paymentMethod: "CASH",
  });

  const emptyDispose = {
    disposalDate: new Date().toISOString().split("T")[0],
    proceeds: 0,
    vatAmount: 0,
    withholdingAmount: 0,
    vatWithholdingAmount: 0,
    paymentMethod: "CASH",
    note: "",
  };

  const num = (v, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const assetCapCost = (a) => {
    if (!a) return 0;
    if (a.capitalizedCost != null && a.capitalizedCost !== "") {
      return num(a.capitalizedCost);
    }
    return num(a.purchaseCost);
  };

  const assetNbv = (a) => {
    if (!a) return 0;
    if (a.netBookValue != null && a.netBookValue !== "") {
      return num(a.netBookValue);
    }
    return Math.max(0, assetCapCost(a) - num(a.accumulatedDepreciation));
  };

  const [disposalForm, setDisposalForm] = useState(emptyDispose);

  const [depreciationForm, setDepreciationForm] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  });

  const [depreciationResult, setDepreciationResult] = useState(null);

  const fetchAssets = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const resp = await apiRequest("assets");
      const data = resp?.data || resp || [];
      setAssets(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || "Failed to load fixed assets.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const loadRelationLookups = async () => {
    try {
      const catResp = await apiRequest("assets/categories?activeOnly=true");
      setCategories(catResp?.data || []);
    } catch {
      /* non-blocking */
    }
  };

  const openModal = async (mode, asset = null) => {
    setModalMode(mode);
    setSelectedAsset(asset);
    setError("");
    setDepreciationResult(null);

    await loadRelationLookups();

    if (mode === "create") {
      setFormData({ ...emptyCreate });
    } else if (asset) {
      setFormData({
        id: asset.id,
        assetCode: asset.assetCode || "",
        name: asset.name || "",
        description: asset.description || "",
        purchaseDate: asset.purchaseDate || "",
        inServiceDate: asset.inServiceDate || "",
        purchaseCost: Number(asset.purchaseCost || 0),
        residualValue: Number(asset.residualValue || 0),
        exciseAmount: Number(asset.exciseAmount || 0),
        vatAmount: Number(asset.vatAmount || 0),
        vatRecoverable: asset.vatRecoverable !== false,
        withholdingAmount: Number(asset.withholdingAmount || 0),
        vatWithholdingAmount: Number(asset.vatWithholdingAmount || 0),
        bankCommission: asset.bankCommission || 0.0,
        paymentMethod: asset.paymentMethod || "CASH",
        reference: asset.reference || "",
        categoryId: asset.categoryId || asset.category?.id || "",
      });
    }

    if (mode === "activate") {
      setActivationForm({ paymentMethod: "CASH" });
    } else if (mode === "dispose") {
      setDisposalForm({ ...emptyDispose });
    }

    setIsModalOpen(true);
  };

  const createCost = Number(formData.purchaseCost || 0);
  const createExcise = Number(formData.exciseAmount || 0);
  const createVat = Number(formData.vatAmount || 0);
  const createWht = Number(formData.withholdingAmount || 0);
  const createVatWht = Number(formData.vatWithholdingAmount || 0);
  // Gross invoice = cost + excise + VAT
  const createGross = createCost + createExcise + createVat;
  // Cash/settlement after withholdings
  const createSettlement = createGross - createWht - createVatWht;
  // Capitalized: cost + excise + non-recoverable VAT
  const createCapitalized =
    createCost + createExcise + (formData.vatRecoverable ? 0 : createVat);

  const handleSaveAsset = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.name.trim()) {
      setError("Asset name is required.");
      return;
    }

    if (!formData.categoryId) {
      setError("Select an asset category.");
      return;
    }

    if (createCost <= 0) {
      setError("Purchase cost (ex-VAT) must be positive.");
      return;
    }

    if (createSettlement < 0) {
      setError(
        "Withholding cannot exceed invoice total (cost + excise + VAT)."
      );
      return;
    }

    const bankCommissionValue =
      formData.paymentMethod === "BANK"
        ? parseFloat(formData.bankCommission) || 0
        : 0;

    if (bankCommissionValue < 0) {
      setError("Bank commission cannot be negative.");
      return;
    }

    try {
      await apiRequest("assets", {
        method: "POST",
        body: {
          assetCode: formData.assetCode.trim(),
          name: formData.name.trim(),
          description: (formData.description || "").trim(),
          purchaseDate: formData.purchaseDate,
          inServiceDate: formData.inServiceDate || formData.purchaseDate,
          purchaseCost: createCost,
          residualValue: Number(formData.residualValue || 0),
          exciseAmount: createExcise,
          vatAmount: createVat,
          vatRecoverable: !!formData.vatRecoverable,
          withholdingAmount: createWht,
          vatWithholdingAmount: createVatWht,
          bankCommission: bankCommissionValue,
          paymentMethod: formData.paymentMethod || "CASH",
          reference: (formData.reference || "").trim(),
          category: { id: parseInt(formData.categoryId, 10) },
        },
      });

      fetchAssets();
      setIsModalOpen(false);
    } catch (err) {
      setError(err?.message || "Failed to register asset.");
    }
  };

  const handleActivateAsset = async (e) => {
    e.preventDefault();
    if (!selectedAsset) return;

    setError("");

    try {
      await apiRequest(`assets/${selectedAsset.id}/activate`, {
        method: "POST",
        body: { paymentMethod: activationForm.paymentMethod },
      });

      fetchAssets();
      setIsModalOpen(false);
    } catch (err) {
      setError(err?.message || "Failed to activate asset.");
    }
  };

  const disposeProc = Number(disposalForm.proceeds || 0);
  const disposeVat = Number(disposalForm.vatAmount || 0);
  const disposeWht = Number(disposalForm.withholdingAmount || 0);
  const disposeVatWht = Number(disposalForm.vatWithholdingAmount || 0);
  const disposeGross = disposeProc + disposeVat;
  const disposeNet = disposeGross - disposeWht - disposeVatWht;

  const selectedNbv = assetNbv(selectedAsset);

  const handleDisposeAsset = async (e) => {
    e.preventDefault();
    if (!selectedAsset) return;

    setError("");

    if (
      disposeProc < 0 ||
      disposeVat < 0 ||
      disposeWht < 0 ||
      disposeVatWht < 0
    ) {
      setError("Amounts cannot be negative.");
      return;
    }

    if (disposeNet < 0) {
      setError("Withholding cannot exceed disposal invoice (proceeds + VAT).");
      return;
    }

    const delta = disposeProc - selectedNbv;

    const outcome =
      delta > 0
        ? `Gain ≈ ${money(delta)} ETB`
        : delta < 0
        ? `Loss ≈ ${money(Math.abs(delta))} ETB`
        : "No gain/loss";

    if (
      !window.confirm(
        `Dispose ${selectedAsset.assetCode}?\nNBV: ${money(
          selectedNbv
        )}\nProceeds (ex-VAT): ${money(
          disposeProc
        )}\n${outcome}\n\nPosts to ledger and cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await apiRequest(`assets/${selectedAsset.id}/dispose`, {
        method: "POST",
        body: {
          disposalDate: disposalForm.disposalDate,
          proceeds: disposeProc,
          vatAmount: disposeVat,
          withholdingAmount: disposeWht,
          vatWithholdingAmount: disposeVatWht,
          paymentMethod: disposalForm.paymentMethod,
          note: (disposalForm.note || "").trim(),
        },
      });

      fetchAssets();
      setIsModalOpen(false);
    } catch (err) {
      setError(err?.message || "Failed to dispose asset.");
    }
  };

  const handleRunDepreciation = async (e) => {
    e.preventDefault();
    setError("");
    setDepreciationResult(null);

    try {
      const resp = await apiRequest(
        `assets/depreciation/run?year=${depreciationForm.year}&month=${depreciationForm.month}`,
        { method: "POST" }
      );

      setDepreciationResult(resp?.data || resp);
      fetchAssets();
    } catch (err) {
      setError(err?.message || "Depreciation run failed.");
    }
  };

  const filteredAssets = assets.filter((ast) => {
    if (!keyword.trim()) return true;

    const q = keyword.toLowerCase();

    return (
      ast.name?.toLowerCase().includes(q) ||
      ast.assetCode?.toLowerCase().includes(q) ||
      ast.categoryName?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-full p-4 md:p-8 font-sans bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <FaBoxes className="text-white text-lg" />
            </div>

            <div>
              <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">
                Fixed Asset Management
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Capitalization (VAT/excise/WH), depreciation, and disposal.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => openModal("depreciate")}
              className="flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-slate-50 text-emerald-700 text-xs font-semibold rounded-xl border border-emerald-200 transition dark:bg-emerald-900/30 dark:hover:bg-emerald-900/50 dark:text-emerald-400 dark:border-emerald-500/20"
            >
              <FaCalculator />
              Run Depreciation
            </button>

            <button
              type="button"
              onClick={() => openModal("create")}
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-xl shadow-lg shadow-emerald-600/20"
            >
              <FaPlus />
              New Asset
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm dark:bg-slate-900/40 dark:border-white/10">
          <div className="relative max-w-md">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-400 text-xs dark:text-slate-500">
              <FaSearch />
            </span>

            <input
              type="text"
              placeholder="Search name, code, category…"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-emerald-500/50 dark:bg-slate-950/50 dark:border-white/10 dark:text-white dark:placeholder-slate-500"
            />
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-xs flex items-center gap-2 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400">
            <FaExclamationTriangle className="shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Asset Table */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm dark:bg-slate-900/40 dark:border-white/10">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1050px] text-left text-xs">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider font-bold dark:border-white/10 dark:bg-slate-950/50 dark:text-slate-400">
                  <th className="px-4 py-4">Asset</th>
                  <th className="px-4 py-4">Code / Category</th>
                  <th className="px-4 py-4 text-right">Cap. cost</th>
                  <th className="px-4 py-4 text-right">Accum. dep.</th>
                  <th className="px-4 py-4 text-right">NBV</th>
                  <th className="px-4 py-4 text-center">Status</th>
                  <th className="px-4 py-4 text-right">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 text-slate-700 dark:divide-white/5 dark:text-slate-300">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center py-10 text-slate-500"
                    >
                      <FaSpinner className="animate-spin inline mr-2 text-emerald-500" />
                      Loading…
                    </td>
                  </tr>
                ) : filteredAssets.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center py-10 text-slate-500"
                    >
                      No assets.
                    </td>
                  </tr>
                ) : (
                  filteredAssets.map((ast) => {
                    const cap = assetCapCost(ast);
                    const accum = num(ast.accumulatedDepreciation);
                    const nbv = assetNbv(ast);
                    const st = String(ast.status || "").toUpperCase();

                    return (
                      <tr
                        key={ast.id}
                        className="hover:bg-slate-50 transition-colors dark:hover:bg-white/[0.02]"
                      >
                        <td className="px-4 py-4 font-semibold text-slate-800 dark:text-white">
                          <div>{ast.name}</div>
                          <div className="text-[10px] text-slate-400 mt-0.5 dark:text-slate-500">
                            In service: {ast.inServiceDate || "—"}
                          </div>
                        </td>

                        <td className="px-4 py-4">
                          <div className="font-mono font-bold text-emerald-600 flex items-center gap-1 dark:text-emerald-400">
                            <FaBarcode className="opacity-40" />
                            {ast.assetCode}
                          </div>

                          <div className="text-[10px] text-slate-500 dark:text-slate-400">
                            {ast.categoryName || "—"}
                          </div>
                        </td>

                        <td className="px-4 py-4 text-right font-mono text-slate-800 dark:text-white">
                          {money(cap)}
                        </td>

                        <td className="px-4 py-4 text-right font-mono text-amber-600 dark:text-amber-400">
                          {money(accum)}
                        </td>

                        <td className="px-4 py-4 text-right font-mono font-bold text-emerald-600 dark:text-emerald-400">
                          {money(nbv)}
                        </td>

                        <td className="px-4 py-4 text-center">
                          <span
                            className={`px-2.5 py-1 rounded-full text-[10px] border ${
                              st === "ACTIVE"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20"
                                : st === "DRAFT"
                                ? "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20"
                                : "bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-700/50 dark:text-slate-400 dark:border-slate-600"
                            }`}
                          >
                            {ast.status}
                          </span>
                        </td>

                        <td className="px-4 py-4 text-right whitespace-nowrap">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => openModal("view", ast)}
                              className="p-2 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition dark:bg-slate-900/60 dark:border-white/10 dark:text-slate-400 dark:hover:bg-white/[0.04] dark:hover:text-slate-200"
                              title="View"
                            >
                              <FaEye />
                            </button>

                            {st === "DRAFT" && (
                              <button
                                type="button"
                                onClick={() => openModal("activate", ast)}
                                className="p-2 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition dark:bg-emerald-600/10 dark:border-emerald-500/10 dark:text-emerald-400 dark:hover:bg-emerald-600/20"
                                title="Activate"
                              >
                                <FaPlayCircle />
                              </button>
                            )}

                            {st === "ACTIVE" && (
                              <button
                                type="button"
                                onClick={() => openModal("dispose", ast)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-red-50 hover:bg-red-100 rounded-lg text-red-600 border border-red-200 text-[10px] font-bold uppercase transition dark:bg-red-600/15 dark:hover:bg-red-600/25 dark:text-red-400 dark:border-red-500/30"
                              >
                                <FaTrashAlt />
                                Dispose
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Bottom Sheet Modal */}
      <div
        className={`fixed inset-0 z-[2000] flex flex-col justify-end transition-opacity duration-300 ${
          isModalOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className={`relative w-full max-w-3xl mx-auto bg-white border border-slate-200 rounded-t-3xl p-6 md:p-8 shadow-[0_-15px_40px_rgba(0,0,0,0.15)] max-h-[90vh] overflow-y-auto space-y-6 transform transition-transform duration-300 dark:bg-slate-900/95 dark:border-white/10 dark:shadow-[0_-15px_40px_rgba(0,0,0,0.5)] ${
            isModalOpen ? "translate-y-0" : "translate-y-full"
          }`}
        >
          {/* Modal Header */}
          <div className="flex justify-between items-start border-b border-slate-200 pb-3 dark:border-white/5">
            <div>
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2 dark:text-white">
                <FaBoxes className="text-emerald-500 text-sm" />

                {modalMode === "create" && "Register fixed asset"}
                {modalMode === "view" && "Asset details"}
                {modalMode === "activate" && "Capitalize & activate"}
                {modalMode === "dispose" && "Dispose asset"}
                {modalMode === "depreciate" && "Run depreciation"}
              </h3>

              <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">
                {modalMode === "create" &&
                  "Register a new fixed asset and its capitalization details."}
                {modalMode === "view" &&
                  "Inspect fixed asset operational and accounting details."}
                {modalMode === "activate" &&
                  "Capitalize the asset and post the settlement."}
                {modalMode === "dispose" &&
                  "Record disposal proceeds and calculate the gain or loss."}
                {modalMode === "depreciate" &&
                  "Run monthly depreciation for active fixed assets."}
              </p>
            </div>

            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-500 border border-slate-200 transition dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 dark:border-white/5"
            >
              <FaTimes />
            </button>
          </div>

          {/* CREATE / VIEW */}
          {(modalMode === "create" || modalMode === "view") && (
            <form onSubmit={handleSaveAsset} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  ["Name", "name", "text"],
                  ["Code (optional)", "assetCode", "text"],
                  ["Reference / serial", "reference", "text"],
                ].map(([label, field, type]) => (
                  <div key={field} className="flex flex-col gap-1.5">
                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
                      {label}
                    </label>

                    <input
                      type={type}
                      required={field === "name"}
                      disabled={modalMode === "view"}
                      value={formData[field]}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          [field]: e.target.value,
                        })
                      }
                      className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-emerald-500/50 disabled:opacity-60 dark:bg-slate-950 dark:border-white/10 dark:text-white dark:placeholder-slate-600"
                    />
                  </div>
                ))}

                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
                    Category
                  </label>

                  <select
                    required
                    disabled={modalMode === "view"}
                    value={formData.categoryId}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        categoryId: e.target.value,
                      })
                    }
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-xs text-slate-800 focus:outline-none focus:border-emerald-500/50 disabled:opacity-60 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                  >
                    <option value="">— Select —</option>

                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({c.statutoryRatePercent}%)
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Financial Inputs – includes Excise */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200 dark:bg-slate-950/30 dark:border-white/5">
                {[
                  ["Purchase cost (VAT-EX)", "purchaseCost", true],
                  ["Residual value", "residualValue", false],
                  ["Excise amount", "exciseAmount", false],
                  ["VAT amount", "vatAmount", false],
                  ["Income withholding", "withholdingAmount", false],
                  ["VAT withholding", "vatWithholdingAmount", false],
                ].map(([label, field, required]) => (
                  <div key={field} className="flex flex-col gap-1.5">
                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
                      {label}
                    </label>

                    <input
                      type="number"
                      step="0.01"
                      required={required}
                      disabled={modalMode === "view"}
                      value={formData[field]}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          [field]: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-mono text-right text-slate-800 focus:outline-none focus:border-emerald-500/50 disabled:opacity-60 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                    />
                  </div>
                ))}

                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer dark:text-slate-300">
                    <input
                      type="checkbox"
                      disabled={modalMode === "view"}
                      checked={!!formData.vatRecoverable}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          vatRecoverable: e.target.checked,
                        })
                      }
                      className="rounded border-slate-300 dark:border-slate-600"
                    />
                    VAT recoverable (input VAT)
                  </label>
                </div>
              </div>

              {/* Create / View Preview – full tax breakdown */}
              {(modalMode === "create" || modalMode === "view") && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 space-y-3 dark:bg-emerald-950/20 dark:border-emerald-500/20">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 dark:text-emerald-400">
                    Amount &amp; taxes breakdown
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-[11px] font-mono text-slate-700 dark:text-slate-300">
                    <div>
                      <span className="text-slate-500 block text-[10px]">
                        Purchase cost
                      </span>
                      {money(createCost)}
                    </div>
                    <div>
                      <span className="text-slate-500 block text-[10px]">
                        Excise
                      </span>
                      {money(createExcise)}
                    </div>
                    <div>
                      <span className="text-slate-500 block text-[10px]">
                        VAT
                      </span>
                      {money(createVat)}
                    </div>
                    <div>
                      <span className="text-slate-500 block text-[10px]">
                        Income WH
                      </span>
                      <span className="text-rose-600 dark:text-rose-400">
                        −{money(createWht)}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500 block text-[10px]">
                        VAT WH
                      </span>
                      <span className="text-rose-600 dark:text-rose-400">
                        −{money(createVatWht)}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500 block text-[10px]">
                        Gross invoice
                      </span>
                      {money(createGross)}
                    </div>
                  </div>

                  <div className="border-t border-emerald-200/60 dark:border-emerald-500/20 pt-3 grid grid-cols-2 sm:grid-cols-3 gap-3 text-[11px] font-mono">
                    <div>
                      <span className="text-slate-500 block text-[10px]">
                        Settlement
                      </span>
                      <span className="font-semibold">
                        {money(createSettlement)}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500 block text-[10px]">
                        Capitalized
                      </span>
                      <span className="text-emerald-600 font-bold dark:text-emerald-400">
                        {money(createCapitalized)}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500 block text-[10px]">
                        Input VAT
                      </span>
                      {formData.vatRecoverable ? money(createVat) : "0.00"}
                    </div>
                  </div>
                </div>
              )}

              {/* Dates */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1 dark:text-slate-400">
                    <FaCalendarAlt className="text-amber-500" />
                    Purchase date
                  </label>

                  <input
                    type="date"
                    required
                    disabled={modalMode === "view"}
                    value={formData.purchaseDate}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        purchaseDate: e.target.value,
                      })
                    }
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2 text-xs text-slate-800 focus:outline-none focus:border-emerald-500/50 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
                    In-service date
                  </label>

                  <input
                    type="date"
                    disabled={modalMode === "view"}
                    value={formData.inServiceDate}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        inServiceDate: e.target.value,
                      })
                    }
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2 text-xs text-slate-800 focus:outline-none focus:border-emerald-500/50 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                  />
                </div>
              </div>

              {/* Payment Method + Bank Commission */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Payment Method */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
                    Payment method
                  </label>

                  <select
                    required
                    disabled={modalMode === "view"}
                    value={formData.paymentMethod}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        paymentMethod: e.target.value,
                        bankCommission:
                          e.target.value === "BANK"
                            ? formData.bankCommission
                            : 0,
                      })
                    }
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-xs text-slate-800 focus:outline-none focus:border-emerald-500/50 disabled:opacity-60 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                  >
                    <option value="CASH">CASH</option>
                    <option value="BANK">BANK</option>
                    <option value="CREDIT">CREDIT (AP)</option>
                  </select>
                </div>

                {/* Bank Commission */}
                {formData.paymentMethod === "BANK" && (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
                      Bank Commission (ETB)
                    </label>

                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      disabled={modalMode === "view"}
                      value={formData.bankCommission ?? ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          bankCommission: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-xs font-mono text-right text-slate-800 placeholder-slate-400 focus:outline-none focus:border-emerald-500/50 disabled:opacity-60 dark:bg-slate-950 dark:border-white/10 dark:text-white dark:placeholder-slate-600"
                    />
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
                  Description
                </label>

                <textarea
                  rows={2}
                  disabled={modalMode === "view"}
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      description: e.target.value,
                    })
                  }
                  className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-xs text-slate-800 resize-none focus:outline-none focus:border-emerald-500/50 disabled:opacity-60 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/5">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-semibold transition dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300"
                >
                  Close
                </button>

                {modalMode === "create" && (
                  <button
                    type="submit"
                    className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold shadow-md shadow-emerald-600/10"
                  >
                    Save draft
                  </button>
                )}
              </div>
            </form>
          )}

          {/* ACTIVATE */}
          {modalMode === "activate" && (
            <form onSubmit={handleActivateAsset} className="space-y-5">
              <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-xl text-xs text-emerald-700 dark:bg-emerald-500/10 dark:border-emerald-500/20 dark:text-emerald-300">
                Posts capitalization using amounts stored on the asset (cost,
                excise, VAT, WH). Choose how settlement was paid.
              </div>

              {selectedAsset && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-[11px] font-mono bg-slate-50 p-4 rounded-xl border border-slate-200 text-slate-700 dark:bg-slate-950/50 dark:border-white/5 dark:text-slate-300">
                  <div>Cap. cost: {money(assetCapCost(selectedAsset))}</div>
                  <div>Excise: {money(selectedAsset.exciseAmount)}</div>
                  <div>VAT: {money(selectedAsset.vatAmount)}</div>
                  <div>WH: {money(selectedAsset.withholdingAmount)}</div>
                  <div>VAT WH: {money(selectedAsset.vatWithholdingAmount)}</div>
                  <div>Settlement: {money(selectedAsset.settlementAmount)}</div>
                </div>
              )}

              <div className="flex flex-col gap-1.5 max-w-xs">
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
                  Payment method
                </label>
                <select
                  required
                  value={activationForm.paymentMethod}
                  onChange={(e) =>
                    setActivationForm({
                      ...activationForm,
                      paymentMethod: e.target.value,
                    })
                  }
                  className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-xs text-slate-800 focus:outline-none focus:border-emerald-500/50 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                >
                  <option value="CASH">CASH</option>
                  <option value="BANK">BANK</option>
                  <option value="CREDIT">CREDIT (AP)</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/5">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-semibold dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold flex items-center gap-2"
                >
                  <FaCheckCircle />
                  Confirm capitalization
                </button>
              </div>
            </form>
          )}

          {/* DISPOSE */}
          {modalMode === "dispose" && (
            <form onSubmit={handleDisposeAsset} className="space-y-5">
              <div className="bg-red-50 border border-red-200 p-4 rounded-xl text-xs text-red-700 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-300">
                Proceeds are <strong>ex-VAT</strong>. Gain/loss uses proceeds vs
                NBV. Net cash = proceeds + VAT − withholdings.
              </div>

              <div className="grid grid-cols-3 gap-3 text-[11px] font-mono bg-slate-50 border border-slate-200 rounded-xl p-4 text-slate-700 dark:bg-slate-950/50 dark:border-white/5 dark:text-slate-300">
                <div>
                  <span className="text-slate-500 text-[10px] block">NBV</span>
                  <span className="text-amber-600 font-bold dark:text-amber-400">
                    {money(selectedNbv)}
                  </span>
                </div>

                <div>
                  <span className="text-slate-500 text-[10px] block">
                    Gross
                  </span>
                  {money(disposeGross)}
                </div>

                <div>
                  <span className="text-slate-500 text-[10px] block">
                    Net cash
                  </span>
                  {money(disposeNet)}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  ["Disposal date", "disposalDate", "date"],
                  ["Proceeds ex-VAT", "proceeds", "number"],
                  ["Output VAT", "vatAmount", "number"],
                  ["Income WH (buyer)", "withholdingAmount", "number"],
                  ["VAT WH (buyer)", "vatWithholdingAmount", "number"],
                ].map(([label, field, type]) => (
                  <div key={field} className="flex flex-col gap-1.5">
                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
                      {label}
                    </label>

                    <input
                      type={type}
                      step={type === "number" ? "0.01" : undefined}
                      min={type === "number" ? "0" : undefined}
                      required={field === "disposalDate"}
                      value={disposalForm[field]}
                      onChange={(e) =>
                        setDisposalForm({
                          ...disposalForm,
                          [field]:
                            type === "number"
                              ? parseFloat(e.target.value) || 0
                              : e.target.value,
                        })
                      }
                      className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-xs text-slate-800 font-mono focus:outline-none focus:border-emerald-500/50 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                    />
                  </div>
                ))}

                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
                    Receive into
                  </label>

                  <select
                    value={disposalForm.paymentMethod}
                    onChange={(e) =>
                      setDisposalForm({
                        ...disposalForm,
                        paymentMethod: e.target.value,
                      })
                    }
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-xs text-slate-800 focus:outline-none focus:border-emerald-500/50 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                  >
                    <option value="CASH">CASH</option>
                    <option value="BANK">BANK</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
                  Note
                </label>

                <input
                  value={disposalForm.note}
                  onChange={(e) =>
                    setDisposalForm({
                      ...disposalForm,
                      note: e.target.value,
                    })
                  }
                  placeholder="Sold / scrapped…"
                  className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-emerald-500/50 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/5">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-semibold dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-xl bg-red-600 hover:bg-red-500 text-white text-xs font-semibold"
                >
                  Confirm disposal
                </button>
              </div>
            </form>
          )}

          {/* DEPRECIATE */}
          {modalMode === "depreciate" && (
            <form onSubmit={handleRunDepreciation} className="space-y-5">
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-xl text-xs text-blue-700 dark:bg-blue-500/10 dark:border-blue-500/20 dark:text-blue-300">
                Monthly depreciation for all active assets in the branch.
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
                    Year
                  </label>

                  <input
                    type="number"
                    required
                    value={depreciationForm.year}
                    onChange={(e) =>
                      setDepreciationForm({
                        ...depreciationForm,
                        year: parseInt(e.target.value, 10),
                      })
                    }
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-xs font-mono text-slate-800 focus:outline-none focus:border-emerald-500/50 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
                    Month
                  </label>

                  <input
                    type="number"
                    min={1}
                    max={12}
                    required
                    value={depreciationForm.month}
                    onChange={(e) =>
                      setDepreciationForm({
                        ...depreciationForm,
                        month: parseInt(e.target.value, 10),
                      })
                    }
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-xs font-mono text-slate-800 focus:outline-none focus:border-emerald-500/50 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                  />
                </div>
              </div>

              {depreciationResult && (
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-xs font-mono text-slate-700 dark:bg-slate-950 dark:border-white/10 dark:text-slate-300">
                  <div className="text-emerald-600 font-bold flex items-center gap-2 dark:text-emerald-400">
                    <FaCheckCircle />
                    {depreciationResult.message || "Posted"}
                  </div>

                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <div>Count: {depreciationResult.depreciatedCount}</div>
                    <div>Skipped: {depreciationResult.skippedCount}</div>
                    <div>
                      Total: {money(depreciationResult.totalAmount)} ETB
                    </div>
                    <div>Ref: {depreciationResult.journalReference || "—"}</div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/5">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-semibold dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300"
                >
                  Close
                </button>

                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold flex items-center gap-2"
                >
                  <FaCalculator />
                  Run
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetManagement; 
